import type { AuthProvider } from '@refinedev/core'
import { StaffRole } from '@/lib/permissions'
import { setUserRole, clearUserRole } from './access-control'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

// Roles that can access the admin portal
const ALLOWED_ROLES: StaffRole[] = [
  StaffRole.ADMIN,
  StaffRole.NURSE,
  StaffRole.DOCTOR,
  StaffRole.SUPPORT,
]

interface AdminUser {
  id: string
  email: string
  name: string | null
  role: StaffRole
  image?: string | null
}

interface LoginResponse {
  accessToken: string
  user: AdminUser
}

// Store user in localStorage
const setStoredUser = (user: AdminUser) => {
  localStorage.setItem('admin_user', JSON.stringify(user))
}

// Get user from localStorage
const getStoredUser = (): AdminUser | null => {
  const stored = localStorage.getItem('admin_user')
  if (!stored) return null
  try {
    return JSON.parse(stored)
  } catch {
    return null
  }
}

// Clear stored user
const clearStoredUser = () => {
  localStorage.removeItem('admin_user')
  localStorage.removeItem('admin_access_token')
}

// Force redirect to login (hard redirect)
const forceLoginRedirect = () => {
  clearStoredUser()
  clearUserRole()
  // Use hard redirect to ensure immediate navigation
  if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
    window.location.href = '/login'
  }
}

// Fetch user info from API
async function fetchMe(): Promise<AdminUser | null> {
  const token = localStorage.getItem('admin_access_token')
  if (!token) return null

  try {
    const res = await fetch(`${API_URL}/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    // Handle 401 - token expired/invalid
    if (res.status === 401) {
      console.log('[authProvider] 401 from /auth/me - token expired')
      forceLoginRedirect()
      return null
    }

    if (!res.ok) return null

    const user = await res.json()
    return user
  } catch {
    return null
  }
}

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      // Credentials login - call NestJS admin login endpoint
      const response = await fetch(`${API_URL}/auth/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        return {
          success: false,
          error: {
            name: 'LoginError',
            message: error.message || 'Invalid email or password',
          },
        }
      }

      const data: LoginResponse = await response.json()

      // Verify user has admin access
      if (!ALLOWED_ROLES.includes(data.user.role)) {
        return {
          success: false,
          error: {
            name: 'AccessDenied',
            message: 'Access denied. Admin portal is for staff only.',
          },
        }
      }

      // Store token and user
      localStorage.setItem('admin_access_token', data.accessToken)
      setStoredUser(data.user)
      setUserRole(data.user.role)

      return {
        success: true,
        redirectTo: '/',
      }
    } catch (error) {
      console.error('Login error:', error)
      return {
        success: false,
        error: {
          name: 'LoginError',
          message: 'An error occurred during login',
        },
      }
    }
  },

  logout: async () => {
    clearUserRole()
    clearStoredUser()

    return {
      success: true,
      redirectTo: '/login',
    }
  },

  check: async () => {
    const token = localStorage.getItem('admin_access_token')

    if (!token) {
      // In Refine v5 the Authenticated wrapper's `redirectOnFail` + the
      // `redirectTo` field from check() weren't kicking in under fresh
      // incognito-style visits to `/` — the page just sat forever on the
      // "Učitavanje..." fallback without ever routing to /login. Guard
      // with a hard window.location redirect so there's always a way out.
      forceLoginRedirect()
      return {
        authenticated: false,
        redirectTo: '/login',
      }
    }

    // Verify token is still valid
    const user = await fetchMe()

    if (!user) {
      clearStoredUser()
      clearUserRole()
      forceLoginRedirect()
      return {
        authenticated: false,
        redirectTo: '/login',
      }
    }

    const role = user.role as StaffRole

    if (!ALLOWED_ROLES.includes(role)) {
      clearStoredUser()
      clearUserRole()
      forceLoginRedirect()
      return {
        authenticated: false,
        redirectTo: '/login',
        error: {
          name: 'AccessDenied',
          message: 'Access denied. Admin portal is for staff only.',
        },
      }
    }

    // Update stored user and role
    setStoredUser(user)
    setUserRole(role)

    return {
      authenticated: true,
    }
  },

  getPermissions: async () => {
    const user = getStoredUser()
    return user?.role || null
  },

  getIdentity: async () => {
    const user = getStoredUser()

    if (!user) {
      return null
    }

    return {
      id: user.id,
      name: user.name || user.email,
      email: user.email,
      avatar: user.image,
      role: user.role,
    }
  },

  onError: async (error) => {
    const status = error?.status || error?.response?.status

    if (status === 401) {
      console.log('[authProvider.onError] 401 detected - forcing logout and redirect')
      forceLoginRedirect()
      return {
        logout: true,
        redirectTo: '/login',
      }
    }

    return { error }
  },
}

// Handle OAuth callback (called from callback route)
export const handleOAuthCallback = async (token: string): Promise<boolean> => {
  try {
    localStorage.setItem('admin_access_token', token)

    const user = await fetchMe()

    if (!user || !ALLOWED_ROLES.includes(user.role)) {
      clearStoredUser()
      return false
    }

    setStoredUser(user)
    setUserRole(user.role)

    return true
  } catch {
    return false
  }
}

